import { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Card, H4, H5, Icon, P1, P3, P4 } from '@moneymade/moneymade-ui'

import {
  getParsedCompareData,
  getTimeAgoValue,
  toFilteredGraphData,
  toFilteredHighLowData,
  toFilterTimeFrames,
  getInitFilterRadioButtonsEmpty,
  setCurrentRadio,
  getCompareDataMany,
  toFilteredHighLowDataMany
} from 'modules/asset/AssetPage/assetPageUtils'
import {
  TIMELINES,
  TIMELINES_CONVERTER,
  TIMELINES_HIGHLOW_CONVERTER,
  ASSET_CLASSES,
  TIMELINES_RETURNS_CONVERTER
} from 'modules/asset/AssetPage/assetPageConstants'
import InfoModal from 'modules/asset/AssetPage/InfoModal/InfoModal'
import Header from 'modules/asset/AssetPage/ReturnsChart/Header/Header'
import Graph from 'modules/asset/AssetPage/ReturnsChart/Graph/Graph'
import HighLow from 'modules/asset/AssetPage/ReturnsChart/HighLow/HighLow'
import Button from 'modules/asset/AssetPage/ReturnsChart/Button/ButtonContainer'
import styles from './ReturnsChart.module.scss'

const ReturnsChart = ({
  isLogged,
  isAuthChecked,
  title,
  infoBlock: { text, image, infoBlockModal, subtitle },
  assetCompare,
  slug,
  slugCurrency,
  slugCompare,
  slugCompareCurrency,
  signature,
  pageSlug,
  stocksData,
  blocks,
  timeframes
}) => {
  const [isModal, setModal] = useState(false)

  // use timeframes from cms only
  const timelines = useMemo(() => timeframes?.map(timeline => TIMELINES_RETURNS_CONVERTER[timeline]), [timeframes])
  // display actual timeframe data

  const timeFrames = useMemo(
    () =>
      Object.keys(assetCompare).length > 0
        ? toFilterTimeFrames(slug, slugCompare, assetCompare, timeframes.length ? timelines : TIMELINES)
        : [],
    [assetCompare, slug, slugCompare, timeframes.length, timelines]
  )
  const [chartTimeline, setChartTimeline] = useState(timeFrames[0] || `1Y`)

  const [prevSumReturnSlug1, setPrevSumReturnSlug1] = useState(0)
  const [prevSumReturnSlug2, setPrevSumReturnSlug2] = useState(0)
  const [graphFocus, setGraphFocus] = useState(false)
  const [isMenu, setMenu] = useState(false)
  const [radioButtons, setRadioButtons] = useState(getInitFilterRadioButtonsEmpty(blocks, isLogged, isAuthChecked))
  const [activeSlug, setActiveSlug] = useState('')

  const slugFirst = useMemo(
    () =>
      !activeSlug
        ? ASSET_CLASSES.find(({ slug: slugLocal }) => slugLocal === slug)?.name
        : blocks?.find(({ slug: blockSlug }) => blockSlug === activeSlug)?.name,
    [activeSlug, blocks, slug]
  )

  const slugSecond = useMemo(
    () => ASSET_CLASSES.find(({ slug: slugLocal }) => slugLocal === (slugCompare || 'spx'))?.name,
    [slugCompare]
  )

  const slugSecondShort = useMemo(() => {
    const slugObject = ASSET_CLASSES.find(({ slug: slugLocal }) => slugLocal === (slugCompare || 'spx'))

    if (slugObject) {
      const { name, shortName } = slugObject
      return shortName || name
    }
    return ''
  }, [slugCompare])

  const compareData = useMemo(
    () =>
      activeSlug
        ? getCompareDataMany(radioButtons, assetCompare, slugCompare)
        : getParsedCompareData(assetCompare, slug, slugCompare || 'spx'),

    [activeSlug, assetCompare, radioButtons, slug, slugCompare]
  )

  const pricesData = useMemo(() => {
    const filteredData = toFilteredGraphData(TIMELINES_CONVERTER[chartTimeline], compareData)
    const { rangeData } = filteredData

    const priceSlug1First = rangeData[0]?.priceSlug1
    const priceSlug1Last = rangeData[(rangeData?.length || 0) - 1]?.priceSlug1

    const priceSlug2First = rangeData[0]?.priceSlug2
    const priceSlug2Last = rangeData[(rangeData?.length || 0) - 1]?.priceSlug2

    return {
      slugFirst: ((priceSlug1Last - priceSlug1First) / priceSlug1First) * 100,
      slugSecond: ((priceSlug2Last - priceSlug2First) / priceSlug2First) * 100
    }
  }, [chartTimeline, compareData])

  useEffect(
    () => setRadioButtons(getInitFilterRadioButtonsEmpty(blocks, isLogged, isAuthChecked)),
    [blocks, isLogged, isAuthChecked]
  )

  const handleSetPrevSum = (returnSlug1, returnSlug2) => {
    setPrevSumReturnSlug1(returnSlug1)
    setPrevSumReturnSlug2(returnSlug2)
  }

  const handleChange = slugChange => {
    setRadioButtons(setCurrentRadio(radioButtons, slugChange))
    setActiveSlug(slugChange)
  }

  return (
    <>
      {infoBlockModal && <InfoModal isOpen={isModal} data={infoBlockModal} handleClose={() => setModal(false)} />}

      <Card className={styles.ReturnsChart}>
        <div className={styles.Header}>
          <div className={styles.LeftSide}>
            {title && (
              <H4 weight="semibold" className={styles.Title}>
                {!activeSlug
                  ? title
                  : `${
                      blocks?.find(({ slug: blockSlug }) => blockSlug === activeSlug)?.name
                    } Returns vs ${slugSecondShort}`}
              </H4>
            )}

            <div className={styles.HeaderMobile}>
              <Header
                pricesData={pricesData}
                prevSumReturn={{ prevSumReturnSlug1, prevSumReturnSlug2 }}
                graphFocus={graphFocus}
                slugLabel={slugSecondShort}
                syncDate={assetCompare?.syncDate}
              />
            </div>
          </div>

          {!!blocks?.length && (
            <div className={styles.FilterBtnMobile}>
              <Button
                key={1}
                blocks={blocks}
                isMenu={isMenu}
                checkboxes={radioButtons}
                setMenu={setMenu}
                name={blocks?.find(({ slug: blockSlug }) => blockSlug === activeSlug)?.name}
                handleChange={handleChange}
              />
            </div>
          )}

          <div className={styles.RightSide}>
            {timeFrames.map((timeline, index) => (
              <button
                key={`timeline-${index}`}
                type="button"
                className={chartTimeline === timeline ? styles.Active : ''}
                onClick={() => setChartTimeline(timeline)}
              >
                {timeline}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.HeaderDynamic}>
          <div className={styles.LeftSide}>
            <Header
              pricesData={pricesData}
              prevSumReturn={{ prevSumReturnSlug1, prevSumReturnSlug2 }}
              graphFocus={graphFocus}
              slugLabel={slugSecondShort}
              syncDate={assetCompare?.syncDate}
            />
          </div>

          {!!blocks?.length && (
            <div className={styles.RightSide}>
              <div className={styles.FilterBtnDesktop}>
                <Button
                  key={2}
                  blocks={blocks}
                  isMenu={isMenu}
                  checkboxes={radioButtons}
                  setMenu={setMenu}
                  name={blocks?.find(({ slug: blockSlug }) => blockSlug === activeSlug)?.name}
                  handleChange={handleChange}
                />
              </div>
            </div>
          )}
        </div>

        <Graph
          chartTimeline={chartTimeline}
          data={toFilteredGraphData(TIMELINES_CONVERTER[chartTimeline], compareData)}
          pricesData={pricesData}
          slugs={{ slugFirst, slugSecond }}
          slugCurrency={slugCurrency}
          handleSetPrevSum={handleSetPrevSum}
          handleGraphFocus={setGraphFocus}
        />

        <div className={styles.Bottom}>
          <div className={styles.LeftSide}>
            {compareData && (
              <HighLow
                slugFirst={slugFirst || slug}
                slugCurrency={slugCurrency}
                slugSecond={slugSecond || slugCompare}
                slugCompareCurrency={slugCompareCurrency}
                data={
                  activeSlug
                    ? toFilteredHighLowDataMany(assetCompare, chartTimeline, activeSlug, slugCompare)
                    : toFilteredHighLowData(TIMELINES_CONVERTER[chartTimeline], compareData)
                }
                timeFrame={TIMELINES_HIGHLOW_CONVERTER[chartTimeline]}
              />
            )}
          </div>

          <div className={styles.RightSide}>
            <div className={styles.Legend}>
              <div className={styles.NameContainer}>
                <img alt="circle" src="https://assets.moneymade.io/images/AssetPage/Circle.svg" />
                <P1
                  className={styles.Name}
                  font={{
                    default: { fontSize: '12px', lineHeight: '16px' }
                  }}
                >
                  {slugFirst || slug}
                </P1>
              </div>
              <div className={styles.NameContainer}>
                <img alt="circle" src="https://assets.moneymade.io/images/AssetPage/CircleDashed.svg" />
                <P1
                  className={styles.Name}
                  font={{
                    default: { fontSize: '12px', lineHeight: '16px' }
                  }}
                >
                  {slugSecond || slugCompare}
                </P1>
              </div>
            </div>

            <div className={styles.Badge}>
              <button type="button" className={styles.InfoBtn} onClick={() => setModal(true)}>
                <Icon icon="icon-info" size={14} />
              </button>

              {pageSlug === 'stocks' ? (
                <>
                  <div className={`${styles.StocksImg} ${stocksData?.trend === 'bull' ? styles.Bull : styles.Bear}`}>
                    <Icon icon={stocksData?.trend === 'bull' ? 'icon-bull' : 'icon-bear'} size={32} />
                  </div>

                  <div className={styles.Texts}>
                    <P4>Performance & sentiment</P4>
                    <H5>{stocksData?.trend === 'bull' ? 'Bull ' : 'Bear '}Market</H5>
                    <P3>{getTimeAgoValue(stocksData?.reversedAt)}</P3>
                  </div>
                </>
              ) : (
                <>
                  {image && (
                    <div className={styles.Img}>
                      <img src={image} alt="icon" />
                    </div>
                  )}

                  <div className={styles.Texts}>
                    {subtitle && <P4>{subtitle}</P4>}
                    {text && <H5>{text}</H5>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {signature && <P4 className={styles.SourceText}>{signature}</P4>}
      </Card>
    </>
  )
}

ReturnsChart.propTypes = {
  isAuthChecked: PropTypes.bool,
  isLogged: PropTypes.bool,
  title: PropTypes.string,
  infoBlock: PropTypes.shape({
    text: PropTypes.string,
    image: PropTypes.string,
    subtitle: PropTypes.string,
    infoBlockModal: PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string
    })
  }),
  assetCompare: PropTypes.shape({
    sixMonthPeriod: PropTypes.shape(PropTypes.object),
    oneYearPeriod: PropTypes.shape(PropTypes.object),
    fiveYearsPeriod: PropTypes.shape(PropTypes.object),
    tenYearsPeriod: PropTypes.shape(PropTypes.object),
    syncDate: PropTypes.string
  }),
  slug: PropTypes.string,
  slugCurrency: PropTypes.string,
  slugCompare: PropTypes.string,
  slugCompareCurrency: PropTypes.string,
  signature: PropTypes.string,
  pageSlug: PropTypes.string,
  stocksData: PropTypes.shape({
    trend: PropTypes.oneOf(['bull', 'bear']),
    reversedAt: PropTypes.string
  }),
  blocks: PropTypes.arrayOf(PropTypes.object),
  timeframes: PropTypes.arrayOf(PropTypes.string)
}

ReturnsChart.defaultProps = {
  isAuthChecked: true,
  isLogged: false,
  title: '',
  infoBlock: {
    text: '',
    image: '',
    subtitle: '',
    infoBlockModal: {
      title: '',
      text: ''
    }
  },
  assetCompare: {},
  slug: '',
  slugCurrency: '',
  slugCompare: '',
  slugCompareCurrency: '',
  signature: '',
  pageSlug: '',
  stocksData: {
    trend: 'bull',
    reversedAt: '0'
  },
  blocks: [],
  timeframes: []
}

export default ReturnsChart
